import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {
  BerthVisit,
  EntryPoint,
  EtaLocations,
  HinterlandEntry,
  PilotStation,
  SeaEntry,
} from '@portbase/bezoekschip-service-typescriptmodels';
import {v4 as uuid} from 'uuid';
import {VisitContext} from '../../../visit-context';
import {AppContext} from '../../../../app-context';
import {sendQuery} from '../../../../common/utils';

/**
 * Lets the user choose a port entry for a visit that was created without one, mirroring the
 * selection on the create-visit screen (entry point / location + ETA). The chosen values are
 * resolved into visitDeclaration.portVisit.portEntry so they are persisted with the regular
 * SaveVisit / DeclareVisit commands (which send the whole visitDeclaration).
 */
@Component({
  selector: 'app-port-entry-create',
  templateUrl: './port-entry-create.component.html',
  styleUrls: ['./port-entry-create.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PortEntryCreateComponent implements OnInit {

  context = VisitContext;

  // undefined when the port has no pilot stations (matches the create-visit screen semantics)
  pilotStations: Observable<PilotStation[]>;
  entryPoints: Observable<EntryPoint[]>;
  locations: Observable<PilotStation[]>;
  pilotBoardingPlace: PilotStation;

  ngOnInit(): void {
    this.normalizeVisitDeclaration();

    // Seed empty option providers so the selects never render with an undefined optionsProvider
    // before GetEtaLocations resolves (app-select throws when it has neither options nor a provider).
    // Keeping pilotStations truthy also hides the entry-point select until we know the real value,
    // matching the create-visit screen.
    this.pilotStations = of([]);
    this.locations = of([]);
    this.entryPoints = undefined;

    const portOfCall = this.context.visit.portOfCall;
    const portUnCode = portOfCall.port.locationUnCode;

    sendQuery('com.portbase.bezoekschip.common.api.visit.GetEtaLocations',
      {
        portUnCode: portUnCode,
        isOrderIncomingMovement: this.context.visit.orderIncomingMovement
      }).subscribe((result: EtaLocations) => {
      this.pilotStations = result.pilotStations && result.pilotStations.length > 0 ? of(result.pilotStations) : undefined;
      this.entryPoints = result.pilotStations && result.pilotStations.length > 0 ? undefined : of(result.entryPoints);
      if (AppContext.isVlissingenOrTerneuzenVisit(portUnCode) && !AppContext.userProfile.organisation.settings?.pilotNorthSeaPortsNauticalServices) {
        this.entryPoints = this.entryPoints.pipe(map(ep => ep.map(e => (<EntryPoint>{
          ...e,
          pilotStation: false
        }))));
        this.pilotStations = undefined;
      }
      if (this.pilotStations) {
        this.locations = this.pilotStations.pipe(map((pilotBoardingPlaces: PilotStation[]) => {
          let allLocations = pilotBoardingPlaces;
          if (this.context.visit.orderIncomingMovement) {
            allLocations = pilotBoardingPlaces.filter(ps => !ps.atSea && ps.name !== 'ACHTERLAND');
            if (portUnCode === 'NLRTM') {
              allLocations.unshift({
                'code': null,
                'name': 'Sea buoy (Maascenter)',
                'atSea': true
              });
            } else if (portUnCode === 'NLAMS') {
              allLocations.unshift({
                'code': null,
                'name': 'Sea buoy (Kruispost)',
                'atSea': true
              });
            }
          }
          return allLocations;
        }));
      } else {
        this.locations = this.entryPoints.pipe(map(e => e.filter(ep => ep.pilotStation)));
      }
    });
  }

  /**
   * A visit created without a port entry (e.g. from a VID) can also be missing the voyage objects
   * that the create-visit flow always seeds. The regular port-entry form hides the carrier/voyage
   * fields when arrivalVoyage is null (app-arrival-voyage uses `@if (voyage)`), which made the form
   * look incomplete compared to a freshly created visit. Seed the same empty objects CreateVisit
   * does so the form is complete once the port entry is chosen.
   */
  private normalizeVisitDeclaration(): void {
    const visitDeclaration = this.context.visit.visitDeclaration;
    if (!visitDeclaration.arrivalVoyage) {
      visitDeclaration.arrivalVoyage = {};
    }
    if (!visitDeclaration.departureVoyage) {
      visitDeclaration.departureVoyage = {};
    }
  }

  entryPointChange(entryPoint: EntryPoint): void {
    if (entryPoint?.pilotStation) {
      this.pilotBoardingPlace = {
        code: entryPoint.code,
        name: entryPoint.name,
        atSea: entryPoint.atSea
      };
    } else {
      this.pilotBoardingPlace = null;
    }
    this.resolvePortEntry();
  }

  selectionChange(): void {
    this.resolvePortEntry();
  }

  isArrived(): boolean {
    return VisitContext.isArrived();
  }

  /**
   * Builds visitDeclaration.portVisit.portEntry from the current entry point / location / ETA
   * selection. Mirrors NewVisitComponent.resolvePortEntry so the produced payload is identical to
   * the create-visit flow. Bails out (leaving portEntry null) while there is not yet a selection
   * that can be resolved, so the visit can still be saved / declared without a port entry.
   */
  private resolvePortEntry(): void {
    const portVisit = this.context.visit.visitDeclaration.portVisit;
    const portUnCode = this.context.visit.portOfCall.port.locationUnCode;
    const useTerminalPlanning = this.context.visit.terminalPlanningEnabled;
    const accessPointsAsPilotBoardingPlaces = VisitContext.accessPointsAsPilotBoardingPlaces(portUnCode);

    // Nothing selected yet -> keep portEntry null so the visit can still be saved without one.
    // A selection is either a pilot boarding place (location) or an entry point; either one must
    // resolve into a port entry so the declare contains it.
    if (!this.pilotBoardingPlace && !portVisit.entryPoint) {
      portVisit.portEntry = null;
      return;
    }

    // A visit created without a port entry may also lack a berth visit. Without one, "Planning based
    // on" is forced to PILOT_BOARDING_PLACE and disabled (planningBasedOnDisabled() is true when there
    // is no first berth), unlike a freshly created visit which always has one seeded berth. Seed the
    // same empty berth visit CreateVisit does so the resolved port entry and the form behave
    // identically. Done here (not on init) so the berth only exists once portEntry is set, avoiding
    // the berth timeline dereferencing a null portEntry.
    if (!portVisit.berthVisits || portVisit.berthVisits.length === 0) {
      portVisit.berthVisits = [<BerthVisit>{id: uuid(), nextMovement: {}, visitPurposes: []}];
    }

    portVisit.firstMovement.order = false;

    // Entry-point based selection (no pilot boarding place chosen). Applies to every incoming-movement
    // port that offers an entry point, not just access-point ports; the SEA/HINTERLAND origin is
    // derived from the entry point. Previously this only ran for access-point ports, so picking an
    // entry point on other ports left portEntry null and DeclareVisit failed with a NullPointerException.
    if (!this.pilotBoardingPlace) {
      portVisit.portEntry = portVisit.entryPoint?.atSea ? <SeaEntry>{
        entryDependency: null,
        earliestTimeOfPortEntry: null,
        etaPilotBoardingPlace: null,
        etaSeaBuoy: portVisit.etaPort,
        requestedEtaPilotBoardingPlace: null,
        intention: 'REQUEST_FOR_ENTRY',
        origin: 'SEA',
        baseForPlanning: useTerminalPlanning ? 'FIRST_BERTH' :
          (portVisit.berthVisits.length > 0 ? null : 'PILOT_BOARDING_PLACE')
      } : <HinterlandEntry>{
        earliestTimeOfPortEntry: null,
        etaPilotBoardingPlace: portVisit.etaPort,
        requestedEtaPilotBoardingPlace: null,
        baseForPlanning: useTerminalPlanning || !this.pilotBoardingPlace ? 'FIRST_BERTH' : 'PILOT_BOARDING_PLACE',
        intention: 'REQUEST_FOR_ENTRY',
        origin: 'HINTERLAND'
      };
      portVisit.pilotStation = this.pilotBoardingPlace;
    } else if (this.pilotBoardingPlace.atSea) {
      portVisit.portEntry = <SeaEntry>{
        entryDependency: null,
        earliestTimeOfPortEntry: null,
        etaPilotBoardingPlace: portVisit.pilotStation && portVisit.pilotStation.code === 'MC'
          ? portVisit.etaPort : null,
        etaSeaBuoy: portVisit.etaPort,
        requestedEtaPilotBoardingPlace: null,
        intention: 'REQUEST_FOR_ENTRY',
        origin: 'SEA',
        baseForPlanning: useTerminalPlanning ? 'FIRST_BERTH' :
          (portVisit.berthVisits.length > 0 ? null : 'PILOT_BOARDING_PLACE')
      };
      if (accessPointsAsPilotBoardingPlaces) {
        portVisit.pilotStation = this.pilotBoardingPlace;
      }
    } else {
      portVisit.portEntry = <HinterlandEntry>{
        earliestTimeOfPortEntry: null,
        etaPilotBoardingPlace: portVisit.etaPort,
        requestedEtaPilotBoardingPlace: null,
        baseForPlanning: useTerminalPlanning ? 'FIRST_BERTH' : 'PILOT_BOARDING_PLACE',
        intention: 'REQUEST_FOR_ENTRY',
        origin: 'HINTERLAND'
      };
      portVisit.pilotStation = this.pilotBoardingPlace;
    }
  }
}
